/**
 * @license
 * Copyright Google Inc. All Rights Reserved.
 *
 * Use of this source code is governed by an MIT-style license that can be
 * found in the LICENSE file at https://angular.io/license
 */


import {Injectable, Renderer2, RendererFactory2, RendererStyleFlags2, RendererType2, ViewEncapsulation} from '@angular/core';

import {EventManager} from './events/event_manager';
import {DomSharedStylesHost} from './shared_styles_host';

export const /** @type {?} */ NAMESPACE_URIS: {[ns: string]: string} = {
  'svg': 'http://www.w3.org/2000/svg',
  'xhtml': 'http://www.w3.org/1999/xhtml',
  'xlink': 'http://www.w3.org/1999/xlink',
  'xml': 'http://www.w3.org/XML/1998/namespace',
  'xmlns': 'http://www.w3.org/2000/xmlns/',
};

const /** @type {?} */ COMPONENT_REGEX = /%COMP%/g;
export const /** @type {?} */ COMPONENT_VARIABLE = '%COMP%';
export const /** @type {?} */ HOST_ATTR = `_nghost-${COMPONENT_VARIABLE}`;
export const /** @type {?} */ CONTENT_ATTR = `_ngcontent-${COMPONENT_VARIABLE}`;
/**
 * @param {?} componentShortId
 * @return {?}
 */
export function shimContentAttribute(componentShortId: string): string {
  return CONTENT_ATTR.replace(COMPONENT_REGEX, componentShortId);
}
/**
 * @param {?} componentShortId
 * @return {?}
 */
export function shimHostAttribute(componentShortId: string): string {
  return HOST_ATTR.replace(COMPONENT_REGEX, componentShortId);
}
/**
 * @param {?} compId
 * @param {?} styles
 * @param {?} target
 * @return {?}
 */
export function flattenStyles(
    compId: string, styles: Array<any|any[]>, target: string[]): string[] {
  for (let /** @type {?} */ i = 0; i < styles.length; i++) {
    let /** @type {?} */ style = styles[i];

    if (Array.isArray(style)) {
      flattenStyles(compId, style, target);
    } else {
      style = style.replace(COMPONENT_REGEX, compId);
      target.push(style);
    }
  }
  return target;
}
/**
 * @param {?} eventHandler
 * @return {?}
 */
function decoratePreventDefault(eventHandler: Function): Function {
  return (event: any) => {
    const /** @type {?} */ allowDefaultBehavior = eventHandler(event);
    if (allowDefaultBehavior === false) {
      // TODO(tbosch): move preventDefault into event plugins...
      event.preventDefault();
      event.returnValue = false;
    }
  };
}
export class DomRendererFactory2 implements RendererFactory2 {
private rendererByCompId = new Map<string, Renderer2>();
private defaultRenderer: Renderer2;
/**
 * @param {?} eventManager
 * @param {?} sharedStylesHost
 */
constructor(private eventManager: EventManager,
private sharedStylesHost: DomSharedStylesHost) {
    this.defaultRenderer = new DefaultDomRenderer2(eventManager);
  };
/**
 * @param {?} element
 * @param {?} type
 * @return {?}
 */
createRenderer(element: any, type: RendererType2|null): Renderer2 {
    if (!element || !type) {
      return this.defaultRenderer;
    }
    switch (type.encapsulation) {
      case ViewEncapsulation.Emulated: {
        let /** @type {?} */ renderer = this.rendererByCompId.get(type.id);
        if (!renderer) {
          renderer =
              new EmulatedEncapsulationDomRenderer2(this.eventManager, this.sharedStylesHost, type);
          this.rendererByCompId.set(type.id, renderer);
        }
        ( /** @type {?} */((<EmulatedEncapsulationDomRenderer2>renderer))).applyToHost(element);
        return renderer;
      }
      case ViewEncapsulation.Native:
        return new ShadowDomRenderer(this.eventManager, this.sharedStylesHost, element, type);
      default: {
        if (!this.rendererByCompId.has(type.id)) {
          const /** @type {?} */ styles = flattenStyles(type.id, type.styles, []);
          this.sharedStylesHost.addStyles(styles);
          this.rendererByCompId.set(type.id, this.defaultRenderer);
        }
        return this.defaultRenderer;
      }
    }
  }
/**
 * @return {?}
 */
begin() {}
/**
 * @return {?}
 */
end() {}
static decorators: DecoratorInvocation[] = [
{ type: Injectable },
];
/**
 * @nocollapse
 */
static ctorParameters: () => ({type: any, decorators?: DecoratorInvocation[]}|null)[] = () => [
{type: EventManager, },
{type: DomSharedStylesHost, },
];
}

function DomRendererFactory2_tsickle_Closure_declarations() {
/** @type {?} */
DomRendererFactory2.decorators;
/**
 * @nocollapse
 * @type {?}
 */
DomRendererFactory2.ctorParameters;
/** @type {?} */
DomRendererFactory2.prototype.rendererByCompId;
/** @type {?} */
DomRendererFactory2.prototype.defaultRenderer;
/** @type {?} */
DomRendererFactory2.prototype.eventManager;
/** @type {?} */
DomRendererFactory2.prototype.sharedStylesHost;
}

class DefaultDomRenderer2 implements Renderer2 {
  data: {[key: string]: any} = Object.create(null);
/**
 * @param {?} eventManager
 */
constructor(private eventManager: EventManager) {}
/**
 * @return {?}
 */
destroy(): void {}

  destroyNode: null;
/**
 * @param {?} name
 * @param {?=} namespace
 * @return {?}
 */
createElement(name: string, namespace?: string): any {
    if (namespace) {
      return document.createElementNS(NAMESPACE_URIS[namespace], name);
    }

    return document.createElement(name);
  }
/**
 * @param {?} value
 * @return {?}
 */
createComment(value: string): any { return document.createComment(value); }
/**
 * @param {?} value
 * @return {?}
 */
createText(value: string): any { return document.createTextNode(value); }
/**
 * @param {?} parent
 * @param {?} newChild
 * @return {?}
 */
appendChild(parent: any, newChild: any): void { parent.appendChild(newChild); }
/**
 * @param {?} parent
 * @param {?} newChild
 * @param {?} refChild
 * @return {?}
 */
insertBefore(parent: any, newChild: any, refChild: any): void {
    if (parent) {
      parent.insertBefore(newChild, refChild);
    }
  }
/**
 * @param {?} parent
 * @param {?} oldChild
 * @return {?}
 */
removeChild(parent: any, oldChild: any): void {
    if (parent) {
      parent.removeChild(oldChild);
    }
  }
/**
 * @param {?} selectorOrNode
 * @return {?}
 */
selectRootElement(selectorOrNode: string|any): any {
    let /** @type {?} */ el: any = typeof selectorOrNode === 'string' ? document.querySelector(selectorOrNode) :
                                                       selectorOrNode;
    if (!el) {
      throw new Error(`The selector "${selectorOrNode}" did not match any elements`);
    }
    el.textContent = '';
    return el;
  }
/**
 * @param {?} node
 * @return {?}
 */
parentNode(node: any): any { return node.parentNode; }
/**
 * @param {?} node
 * @return {?}
 */
nextSibling(node: any): any { return node.nextSibling; }
/**
 * @param {?} el
 * @param {?} name
 * @param {?} value
 * @param {?=} namespace
 * @return {?}
 */
setAttribute(el: any, name: string, value: string, namespace?: string): void {
    if (namespace) {
      name = `${namespace}:${name}`;
      const /** @type {?} */ namespaceUri = NAMESPACE_URIS[namespace];
      if (namespaceUri) {
        el.setAttributeNS(namespaceUri, name, value);
      } else {
        el.setAttribute(name, value);
      }
    } else {
      el.setAttribute(name, value);
    }
  }
/**
 * @param {?} el
 * @param {?} name
 * @param {?=} namespace
 * @return {?}
 */
removeAttribute(el: any, name: string, namespace?: string): void {
    if (namespace) {
      const /** @type {?} */ namespaceUri = NAMESPACE_URIS[namespace];
      if (namespaceUri) {
        el.removeAttributeNS(namespaceUri, name);
      } else {
        el.removeAttribute(`${namespace}:${name}`);
      }
    } else {
      el.removeAttribute(name);
    }
  }
/**
 * @param {?} el
 * @param {?} name
 * @return {?}
 */
addClass(el: any, name: string): void { el.classList.add(name); }
/**
 * @param {?} el
 * @param {?} name
 * @return {?}
 */
removeClass(el: any, name: string): void { el.classList.remove(name); }
/**
 * @param {?} el
 * @param {?} style
 * @param {?} value
 * @param {?} flags
 * @return {?}
 */
setStyle(el: any, style: string, value: any, flags: RendererStyleFlags2): void {
    if (flags & RendererStyleFlags2.DashCase) {
      el.style.setProperty(
          style, value, !!(flags & RendererStyleFlags2.Important) ? 'important' : '');
    } else {
      el.style[style] = value;
    }
  }
/**
 * @param {?} el
 * @param {?} style
 * @param {?} flags
 * @return {?}
 */
removeStyle(el: any, style: string, flags: RendererStyleFlags2): void {
    if (flags & RendererStyleFlags2.DashCase) {
      el.style.removeProperty(style);
    } else {
      // IE requires '' instead of null
      // see https://github.com/angular/angular/issues/7916
      el.style[style] = '';
    }
  }
/**
 * @param {?} el
 * @param {?} name
 * @param {?} value
 * @return {?}
 */
setProperty(el: any, name: string, value: any): void {
    checkNoSyntheticProp(name, 'property');
    el[name] = value;
  }
/**
 * @param {?} node
 * @param {?} value
 * @return {?}
 */
setValue(node: any, value: string): void { node.nodeValue = value; }
/**
 * @param {?} target
 * @param {?} event
 * @param {?} callback
 * @return {?}
 */
listen(target: 'window'|'document'|'body'|any, event: string, callback: (event: any) => boolean):
      () => void {
    checkNoSyntheticProp(event, 'listener');
    if (typeof target === 'string') {
      return /** @type {?} */(( <() => void>this.eventManager.addGlobalEventListener(
          target, event, decoratePreventDefault(callback))));
    }
    return /** @type {?} */(( /** @type {?} */(( <() => void>this.eventManager.addEventListener(
               target, event, decoratePreventDefault(callback)))) as() => void));
  }
}

function DefaultDomRenderer2_tsickle_Closure_declarations() {
/** @type {?} */
DefaultDomRenderer2.prototype.data;
/** @type {?} */
DefaultDomRenderer2.prototype.destroyNode;
/** @type {?} */
DefaultDomRenderer2.prototype.eventManager;
}


const /** @type {?} */ AT_CHARCODE = '@'.charCodeAt(0);
/**
 * @param {?} name
 * @param {?} nameKind
 * @return {?}
 */
function checkNoSyntheticProp(name: string, nameKind: string) {
  if (name.charCodeAt(0) === AT_CHARCODE) {
    throw new Error(
        `Found the synthetic ${nameKind} ${name}. Please include either "BrowserAnimationsModule" or "NoopAnimationsModule" in your application.`);
  }
}
class EmulatedEncapsulationDomRenderer2 extends DefaultDomRenderer2 {
private contentAttr: string;
private hostAttr: string;
/**
 * @param {?} eventManager
 * @param {?} sharedStylesHost
 * @param {?} component
 */
constructor(
      eventManager: EventManager, sharedStylesHost: DomSharedStylesHost,
private component: RendererType2) {
    super(eventManager);
    const styles = flattenStyles(component.id, component.styles, []);
    sharedStylesHost.addStyles(styles);

    this.contentAttr = shimContentAttribute(component.id);
    this.hostAttr = shimHostAttribute(component.id);
  }
/**
 * @param {?} element
 * @return {?}
 */
applyToHost(element: any) { super.setAttribute(element, this.hostAttr, ''); }
/**
 * @param {?} parent
 * @param {?} name
 * @return {?}
 */
createElement(parent: any, name: string): Element {
    const /** @type {?} */ el = super.createElement(parent, name);
    super.setAttribute(el, this.contentAttr, '');
    return el;
  }
}

function EmulatedEncapsulationDomRenderer2_tsickle_Closure_declarations() {
/** @type {?} */
EmulatedEncapsulationDomRenderer2.prototype.contentAttr;
/** @type {?} */
EmulatedEncapsulationDomRenderer2.prototype.hostAttr;
/** @type {?} */
EmulatedEncapsulationDomRenderer2.prototype.component;
}

class ShadowDomRenderer extends DefaultDomRenderer2 {
private shadowRoot: any;
/**
 * @param {?} eventManager
 * @param {?} sharedStylesHost
 * @param {?} hostEl
 * @param {?} component
 */
constructor(
      eventManager: EventManager,
private sharedStylesHost: DomSharedStylesHost,
private hostEl: any,
private component: RendererType2) {
    super(eventManager);
    this.shadowRoot = (hostEl as any).createShadowRoot();
    this.sharedStylesHost.addHost(this.shadowRoot);
    const styles = flattenStyles(component.id, component.styles, []);
    for (let i = 0; i < styles.length; i++) {
      const styleEl = document.createElement('style');
      styleEl.textContent = styles[i];
      this.shadowRoot.appendChild(styleEl);
    }
  }
/**
 * @param {?} node
 * @return {?}
 */
private nodeOrShadowRoot(node: any): any { return node === this.hostEl ? this.shadowRoot : node; }
/**
 * @return {?}
 */
destroy() { this.sharedStylesHost.removeHost(this.shadowRoot); }
/**
 * @param {?} parent
 * @param {?} newChild
 * @return {?}
 */
appendChild(parent: any, newChild: any): void {
    return super.appendChild(this.nodeOrShadowRoot(parent), newChild);
  }
/**
 * @param {?} parent
 * @param {?} newChild
 * @param {?} refChild
 * @return {?}
 */
insertBefore(parent: any, newChild: any, refChild: any): void {
    return super.insertBefore(this.nodeOrShadowRoot(parent), newChild, refChild);
  }
/**
 * @param {?} parent
 * @param {?} oldChild
 * @return {?}
 */
removeChild(parent: any, oldChild: any): void {
    return super.removeChild(this.nodeOrShadowRoot(parent), oldChild);
  }
/**
 * @param {?} node
 * @return {?}
 */
parentNode(node: any): any {
    return this.nodeOrShadowRoot(super.parentNode(this.nodeOrShadowRoot(node)));
  }
}

function ShadowDomRenderer_tsickle_Closure_declarations() {
/** @type {?} */
ShadowDomRenderer.prototype.shadowRoot;
/** @type {?} */
ShadowDomRenderer.prototype.sharedStylesHost;
/** @type {?} */
ShadowDomRenderer.prototype.hostEl;
/** @type {?} */
ShadowDomRenderer.prototype.component;
}


interface DecoratorInvocation {
  type: Function;
  args?: any[];
}



// WEBPACK FOOTER //
// ../../../../packages/platform-browser/src/dom/dom_renderer.ts